import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Radio, Copy, CheckCircle2, ChevronDown, ChevronUp, Loader2, Save,
} from "lucide-react";
import {
  inboxSettings, inboxRoutingSettings, inboxRoutingSave,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * InboxLiveWireCard — the plain-English inbox setup panel:
 *   1. Shows the customer's own forwarding address (inbox+SLUG@…).
 *   2. Shows a "Copy" button and step-by-step instructions.
 *   3. Manages lead-routing settings (who handles which type of message,
 *      and which ones should trigger an urgent alert).
 *
 * The point of this card is to make the difference between "demo inbox"
 * and "live inbox" as tap-to-copy simple as possible.
 */
export default function InboxLiveWireCard() {
  const [s, setS] = useState(null);
  const [r, setR] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    inboxSettings().then(setS).catch(() => setS(null));
    inboxRoutingSettings().then(setR).catch(() => setR(null));
  }, []);

  const copyAddr = async () => {
    try {
      await navigator.clipboard.writeText(s.forwarding_address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const saveRouting = async () => {
    setBusy(true);
    try {
      await inboxRoutingSave({
        routes: r.routes,
        assignees: r.assignees,
      });
      toast.success("Routing saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!s) return null;

  return (
    <Card
      className="border border-slate-200 bg-white p-5"
      data-testid="inbox-live-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            <Radio className="h-4 w-4" /> Live inbox
          </div>
          <h3 className="mt-1 font-heading text-lg font-bold text-slate-900">
            Turn on real replies — forward your support mailbox here
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Point your existing support inbox at the address below and every
            real reply will show up in this Unified Inbox in seconds.
            No new provider, no coding.
          </p>
        </div>
        <Badge
          variant="outline"
          className={s.inbound_configured
            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
            : "border-amber-300 bg-amber-50 text-amber-800"}
          data-testid="inbox-live-badge"
        >
          {s.inbound_configured ? "Ready" : "Setup required"}
        </Badge>
      </div>

      {/* Address + copy */}
      <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="text-xs font-semibold text-slate-500">
          Your inbox address
        </div>
        <div className="mt-1 flex items-center gap-2">
          <code
            className="flex-1 truncate rounded bg-white px-2 py-1.5 font-mono text-sm text-slate-900"
            data-testid="inbox-forwarding-addr"
          >
            {s.forwarding_address}
          </code>
          <Button
            size="sm" variant="outline" onClick={copyAddr}
            data-testid="inbox-copy-addr"
          >
            {copied ? <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-3 flex items-center gap-1 text-sm font-semibold text-orange-600 hover:underline"
        data-testid="inbox-toggle-details"
      >
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {expanded ? "Hide setup instructions" : "Show setup instructions"}
      </button>

      {expanded && (
        <div className="mt-3 rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700">
          <ol className="ml-5 list-decimal space-y-1">
            {s.how_to.map((line, i) => <li key={i}>{line}</li>)}
          </ol>
          <p className="mt-3 text-xs text-slate-500">
            {s.provider_upgrade_path}
          </p>
        </div>
      )}

      {/* Routing settings */}
      {r && (
        <div className="mt-5 border-t border-slate-100 pt-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Auto-routing
              </div>
              <h4 className="mt-1 text-sm font-bold text-slate-900">
                Who handles what — and when to raise an alert
              </h4>
            </div>
            <Button
              size="sm"
              className="bg-orange-600 text-white hover:bg-orange-700"
              onClick={saveRouting} disabled={busy}
              data-testid="routing-save-btn"
            >
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
              Save
            </Button>
          </div>

          {/* Assignees per role */}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {r.roles.filter((role) => role !== "owner").map((role) => (
              <div key={role}>
                <label className="text-xs font-semibold text-slate-500 capitalize">
                  {role} email
                </label>
                <input
                  type="email"
                  value={r.assignees?.[role] || ""}
                  placeholder={`${role}@yourbusiness.com`}
                  onChange={(e) => setR({
                    ...r,
                    assignees: { ...r.assignees, [role]: e.target.value }
                  })}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  data-testid={`routing-assignee-${role}`}
                />
              </div>
            ))}
          </div>

          {/* Route per intent */}
          <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2">Intent</th>
                  <th className="px-3 py-2">Assigned to</th>
                  <th className="px-3 py-2">Urgent alert</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {r.intents.map((intent) => (
                  <tr key={intent}>
                    <td className="px-3 py-2 capitalize">{intent.replace(/_/g, " ")}</td>
                    <td className="px-3 py-2">
                      <select
                        value={r.routes?.[intent]?.role || "owner"}
                        onChange={(e) => setR({
                          ...r,
                          routes: {
                            ...r.routes,
                            [intent]: {
                              role: e.target.value,
                              notify_urgent: r.routes?.[intent]?.notify_urgent || false,
                            }
                          }
                        })}
                        className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                        data-testid={`route-role-${intent}`}
                      >
                        {r.roles.map((role) => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <label className="inline-flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={r.routes?.[intent]?.notify_urgent || false}
                          onChange={(e) => setR({
                            ...r,
                            routes: {
                              ...r.routes,
                              [intent]: {
                                role: r.routes?.[intent]?.role || "owner",
                                notify_urgent: e.target.checked,
                              }
                            }
                          })}
                          data-testid={`route-alert-${intent}`}
                        />
                        Notify when urgent
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            AI classifies every new message on arrival — you can override it
            per-thread. Urgent alerts appear here inside the app for the
            assignee.
          </p>
        </div>
      )}
    </Card>
  );
}
